'use client'

import React, { useState } from 'react'
import { Icons } from '@/components/ui/icons'
import { Button } from '@/components/ui/button'

// ============================================================================
// Types
// ============================================================================

interface CodeBlockData {
  code: string
  language?: string
  filename?: string
  highlightLines?: number[]
}

interface CodeBlockWidgetProps {
  data: CodeBlockData
  title?: string
}

// ============================================================================
// Code Block Widget
// ============================================================================

export function CodeBlockWidget({ data, title }: CodeBlockWidgetProps) {
  const [copied, setCopied] = useState(false)
  const { code, language = 'text', filename, highlightLines } = data

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy code:', error)
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Icons.code className="h-4 w-4 text-gray-600" />
          {title && <h3 className="font-semibold text-brand-navy text-sm">{title}</h3>}
          {filename && !title && <span className="text-sm text-gray-600">{filename}</span>}
          {language && (
            <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-700 font-mono">
              {language}
            </span>
          )}
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 gap-1.5 text-xs"
        >
          {copied ? (
            <>
              <Icons.check className="h-3 w-3" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Icons.copy className="h-3 w-3" />
              <span>Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Code Content */}
      <div className="overflow-x-auto">
        <pre className="p-4 text-sm font-mono bg-gray-900 text-gray-100">
          <code className={`language-${language}`}>
            {highlightLines ? renderHighlightedCode(code, highlightLines) : code}
          </code>
        </pre>
      </div>
    </div>
  )
}

// ============================================================================
// Helper Functions
// ============================================================================

function renderHighlightedCode(code: string, highlightLines: number[]): React.ReactNode {
  const lines = code.split('\n')
  
  return lines.map((line, index) => {
    const lineNumber = index + 1
    const isHighlighted = highlightLines.includes(lineNumber)
    
    return (
      <div
        key={index}
        className={isHighlighted ? 'bg-yellow-500/20 -mx-4 px-4' : ''}
      >
        {line}
        {index < lines.length - 1 && '\n'}
      </div>
    )
  })
}
